/**
 * Configuration registry mapping every POM-QM module key to its setup modal schema.
 * Supports all 37 frontend modules with full field definitions, validations, and naming rules.
 */
export const MODULE_MODAL_CONFIGS = {
  // ABC Analysis
  abc: {
    title: 'Create Data Set for ABC Analysis',
    fields: [
      { name: 'itemCount', label: 'Number of Items', type: 'number', min: 2, default: 5 }
    ],
    hasRowNaming: true,
    rowPrefix: 'Item'
  },

  // Decision Tables
  decision_table: {
    title: 'Create Data Set for Decision Tables',
    fields: [
      { name: 'numRows', label: 'Number of Options', type: 'number', min: 2, default: 4, span: 12 },
      { name: 'numCols', label: 'Number of Scenarios', type: 'number', min: 2, default: 4, span: 12 }
    ],
    hasRowNaming: true,
    rowNamingLabel: 'Row Names (Options)',
    rowPrefix: 'Option',
    hasColNaming: true,
    colNamingLabel: 'Column Names (Scenarios)',
    colPrefix: 'Scenario',
    allowMonths: true
  },

  // Binomial Distribution
  binomial: {
    title: 'Binomial Distribution Setup',
    fields: [
      { name: 'number_of_trials', label: 'Number of Trials (n)', type: 'number', min: 0, precision: 0, default: 10 },
      { name: 'probability_of_success', label: 'Probability of Success (p)', type: 'number', min: 0, max: 1, step: 0.01, default: 0.5 },
      { name: 'specific_number_r', label: 'Specific Number (r)', type: 'number', min: 0, precision: 0, default: 2 }
    ]
  },

  // Poisson Distribution
  poisson: {
    title: 'Poisson Distribution Setup',
    fields: [
      { name: 'mean_rate', label: 'Mean Rate (λ)', type: 'number', min: 0.001, step: 0.1, default: 2 },
      { name: 'specific_number_r', label: 'Specific Number (r)', type: 'number', min: 0, precision: 0, default: 1 }
    ]
  },

  // Transport Analysis
  transport: {
    title: 'Create Transport Analysis Data Set',
    fields: [
      { name: 'numSources', label: 'Number of Sources', type: 'number', min: 1, default: 3, span: 12 },
      { name: 'numDestinations', label: 'Number of Destinations', type: 'number', min: 1, default: 3, span: 12 }
    ]
  },

  // Transport Model (Alternative view)
  transport_model: {
    title: 'Create Transport Model Data Set',
    fields: [
      { name: 'numSources', label: 'Number of Sources', type: 'number', min: 1, default: 3, span: 12 },
      { name: 'numDestinations', label: 'Number of Destinations', type: 'number', min: 1, default: 3, span: 12 }
    ]
  },

  // Preference Matrix
  preference_matrix: {
    title: 'Preference Matrix Setup',
    fields: [
      { name: 'numAlternatives', label: 'Number of Alternatives', type: 'number', min: 2, default: 3, span: 12 },
      { name: 'numCriteria', label: 'Number of Criteria', type: 'number', min: 1, default: 3, span: 12 }
    ],
    hasRowNaming: true,
    rowPrefix: 'Alternative',
    hasColNaming: true,
    colPrefix: 'Criterion'
  },

  // Single Server Model (Queueing M/M/1)
  single_server: {
    title: 'Single Server Model Setup (M/M/1)',
    fields: [
      { name: 'arrival_rate', label: 'Arrival Rate (λ)', type: 'number', min: 0.001, step: 0.1, default: 5 },
      { name: 'service_rate', label: 'Service Rate (μ)', type: 'number', min: 0.001, step: 0.1, default: 8 }
    ]
  },

  // Multiple Server Model (Queueing M/M/s)
  multiple_server: {
    title: 'Multiple Server Model Setup (M/M/s)',
    fields: [
      { name: 'arrival_rate', label: 'Arrival Rate (λ)', type: 'number', min: 0.001, step: 0.1, default: 10, span: 12 },
      { name: 'service_rate', label: 'Service Rate (μ)', type: 'number', min: 0.001, step: 0.1, default: 4, span: 12 },
      { name: 'number_of_servers', label: 'Number of Servers (s)', type: 'number', min: 1, precision: 0, default: 3 }
    ]
  },

  // Aggregate Planning
  aggregate_planning: {
    title: 'Aggregate Planning Setup',
    fields: [
      { name: 'periods', label: 'Number of Periods', type: 'number', min: 1, max: 24, default: 6 },
      {
        name: 'allow_backorders',
        label: 'Backorders Allowed',
        type: 'radio',
        default: false,
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false }
        ]
      }
    ]
  },

  // Crossover Analysis
  crossover: {
    title: 'Crossover Analysis Setup',
    fields: [
      { name: 'numProcesses', label: 'Number of Processes', type: 'number', min: 2, default: 3 }
    ],
    hasRowNaming: true,
    rowPrefix: 'Process'
  },

  // Multiproduct Break-Even
  multiproduct: {
    title: 'Multiproduct Break-Even Analysis Setup',
    fields: [
      { name: 'numProducts', label: 'Number of Products', type: 'number', min: 1, default: 4 }
    ],
    hasRowNaming: true,
    rowPrefix: 'Product'
  },

  // Kanban Computation
  kanban: {
    title: 'Kanban Computation Setup',
    fields: [
      { name: 'daily_demand', label: 'Daily Demand', type: 'number', min: 1, default: 500, span: 12 },
      { name: 'lead_time', label: 'Lead Time (Days)', type: 'number', min: 0.01, step: 0.1, default: 2, span: 12 },
      { name: 'safety_stock_percent', label: 'Safety Stock (%)', type: 'number', min: 0, max: 100, default: 10, span: 12 },
      { name: 'container_size', label: 'Container Size', type: 'number', min: 1, default: 50, span: 12 }
    ]
  },

  // P Chart Module (Control Charts)
  p_chart: {
    title: 'P-Chart Quality Control Setup',
    fields: [
      { name: 'numSamples', label: 'Number of Samples', type: 'number', min: 2, default: 10, span: 12 },
      { name: 'sampleSize', label: 'Sample Size (n)', type: 'number', min: 1, default: 100, span: 12 }
    ]
  },

  // X-Bar Charts
  x_bar_chart: {
    title: 'X-Bar Chart Quality Control Setup',
    fields: [
      { name: 'numSamples', label: 'Number of Samples', type: 'number', min: 2, default: 10, span: 12 },
      { name: 'sampleSize', label: 'Observations per Sample', type: 'number', min: 2, max: 25, default: 5, span: 12 }
    ]
  },

  // Determine Time (Learning Curve)
  determine_time: {
    title: 'Learning Curve Computation Setup',
    fields: [
      { name: 'time_first_unit', label: 'Time for First Unit (T1)', type: 'number', min: 0.01, default: 100, span: 12 },
      { name: 'learning_rate', label: 'Learning Rate (%)', type: 'number', min: 1, max: 100, default: 80, span: 12 },
      { name: 'unit_number', label: 'Target Unit Number (N)', type: 'number', min: 1, default: 10 }
    ]
  },

  // Markov Analysis
  markov: {
    title: 'Markov Analysis Setup',
    fields: [
      { name: 'numStates', label: 'Number of States', type: 'number', min: 2, max: 10, default: 3, span: 12 },
      { name: 'numPeriods', label: 'Number of Periods to Project', type: 'number', min: 1, default: 5, span: 12 }
    ]
  },

  // Erlang K Service Times (M/E_k/1)
  erlang_k: {
    title: 'Erlang K Service Times Setup (M/E_k/1)',
    fields: [
      { name: 'arrival_rate', label: 'Arrival Rate (λ)', type: 'number', min: 0.001, step: 0.1, default: 4, span: 12 },
      { name: 'service_rate', label: 'Service Rate (μ)', type: 'number', min: 0.001, step: 0.1, default: 6, span: 12 },
      { name: 'k_shape', label: 'Erlang Shape Parameter (k)', type: 'number', min: 1, precision: 0, default: 3 }
    ]
  },

  // Finite Source Queueing (M/M/s/N/N)
  finite_source: {
    title: 'Finite Source Queueing Setup',
    fields: [
      { name: 'arrival_rate', label: 'Arrival Rate per Customer (λ)', type: 'number', min: 0.001, step: 0.1, default: 0.1, span: 12 },
      { name: 'service_rate', label: 'Service Rate per Server (μ)', type: 'number', min: 0.001, step: 0.1, default: 1.0, span: 12 },
      { name: 'population_size', label: 'Customer Population Size (N)', type: 'number', min: 1, precision: 0, default: 20, span: 12 },
      { name: 'num_servers', label: 'Number of Service Channels (s)', type: 'number', min: 1, precision: 0, default: 2, span: 12 }
    ]
  },

  // Error Analysis (Forecasting Accuracy)
  error_analysis: {
    title: 'Forecasting Error Analysis Setup',
    fields: [
      { name: 'numPeriods', label: 'Number of Historical Periods', type: 'number', min: 2, default: 6 }
    ]
  },

  // Regression Projector
  regression: {
    title: 'Regression Analysis Setup',
    fields: [
      { name: 'numPoints', label: 'Number of Data Points', type: 'number', min: 2, default: 6 }
    ]
  },

  // Time Study
  time_study: {
    title: 'Work Measurement Time Study Setup',
    fields: [
      { name: 'numElements', label: 'Number of Work Elements', type: 'number', min: 1, default: 4, span: 12 },
      { name: 'numCycles', label: 'Number of Observed Cycles', type: 'number', min: 1, default: 5, span: 12 }
    ]
  },

  // Sample Size Time Study
  sample_size_time_study: {
    title: 'Sample Size Determination for Time Study',
    fields: [
      { name: 'desired_accuracy', label: 'Desired Accuracy (%)', type: 'number', min: 0.01, max: 50, default: 5, span: 12 },
      {
        name: 'confidence_level',
        label: 'Confidence Level',
        type: 'select',
        default: 95,
        span: 12,
        options: [
          { label: '90%', value: 90 },
          { label: '95%', value: 95 },
          { label: '99%', value: 99 }
        ]
      }
    ]
  },

  // Quantity Discount EOQ
  quantity_discount: {
    title: 'Quantity Discount EOQ Setup',
    fields: [
      { name: 'annual_demand', label: 'Annual Demand (D)', type: 'number', min: 1, default: 5000, span: 12 },
      { name: 'order_cost', label: 'Ordering / Setup Cost (S)', type: 'number', min: 0, default: 49, span: 12 },
      { name: 'holding_cost_percent', label: 'Annual Holding Cost (%)', type: 'number', min: 0, max: 100, default: 20, span: 12 },
      { name: 'numTiers', label: 'Number of Price Tiers', type: 'number', min: 1, default: 3, span: 12 }
    ]
  },

  // Single Period Inventory (Discrete)
  single_period_discrete: {
    title: 'Single Period Inventory Setup (Discrete)',
    fields: [
      { name: 'price', label: 'Selling Price per Unit', type: 'number', min: 0, default: 10, span: 12 },
      { name: 'cost', label: 'Cost per Unit', type: 'number', min: 0, default: 6, span: 12 },
      { name: 'salvage', label: 'Salvage Value per Unit', type: 'number', min: 0, default: 2, span: 12 },
      { name: 'numRows', label: 'Number of Demand Levels', type: 'number', min: 1, default: 4, span: 12 }
    ]
  },

  // Single Period Inventory (Normal)
  single_period_normal: {
    title: 'Single Period Inventory Setup (Normal Distribution)',
    fields: [
      { name: 'mean', label: 'Mean Demand (μ)', type: 'number', min: 0, default: 100, span: 12 },
      { name: 'std_dev', label: 'Standard Deviation (σ)', type: 'number', min: 0.0001, default: 15, span: 12 },
      { name: 'price', label: 'Selling Price per Unit', type: 'number', min: 0, default: 10, span: 12 },
      { name: 'cost', label: 'Cost per Unit', type: 'number', min: 0, default: 6, span: 12 },
      { name: 'salvage', label: 'Salvage Value per Unit', type: 'number', min: 0, default: 2, span: 12 }
    ]
  },

  // Least Squares Regression
  least_squares: {
    title: 'Least Squares Trend Analysis Setup',
    fields: [
      { name: 'numPoints', label: 'Number of Historical Periods', type: 'number', min: 2, default: 6 }
    ]
  },

  // PM Mean & Std Dev (Project Management / PERT)
  pm_mean_std_dev: {
    title: 'Project Management PERT Setup',
    fields: [
      { name: 'numTasks', label: 'Number of Tasks / Activities', type: 'number', min: 1, default: 5 }
    ]
  },

  // Reorder Point & Safety Stock
  reorder_point_safety_stock: {
    title: 'Reorder Point & Safety Stock Setup',
    fields: [
      { name: 'daily_demand', label: 'Average Daily Demand', type: 'number', min: 0, default: 50, span: 12 },
      { name: 'lead_time', label: 'Lead Time (Days)', type: 'number', min: 0, default: 4, span: 12 },
      { name: 'service_level', label: 'Desired Service Level (%)', type: 'number', min: 50, max: 99.9, default: 95, span: 24 }
    ]
  },

  // Reorder Point / Safety Stock (Discrete)
  reorder_point_discrete: {
    title: 'Reorder Point / Safety Stock (Discrete) Setup',
    fields: [
      { name: 'numRows', label: 'Number of Demand Levels', type: 'number', min: 1, default: 4, span: 24 }
    ]
  },

  // Time Series Analysis
  time_series: {
    title: 'Time Series Forecasting Setup',
    fields: [
      { name: 'numPeriods', label: 'Number of Historical Periods', type: 'number', min: 2, default: 6 }
    ]
  },

  // Center of Gravity Location
  center_of_gravity: {
    title: 'Center of Gravity Facility Location Setup',
    fields: [
      { name: 'numLocations', label: 'Number of Supply / Demand Locations', type: 'number', min: 2, default: 4 }
    ]
  },

  // Normal Distribution
  normal_dist: {
    title: 'Normal Distribution Probability Setup',
    fields: [
      { name: 'mean', label: 'Mean (μ)', type: 'number', min: 0, default: 100, span: 12 },
      { name: 'std_dev', label: 'Standard Deviation (σ)', type: 'number', min: 0.0001, default: 15, span: 12 },
      { name: 'x_value', label: 'Target Value (X)', type: 'number', min: 0, default: 110, span: 24 }
    ]
  },

  // Process Capability Cpk
  cpk: {
    title: 'Process Capability (Cpk) Setup',
    fields: [
      { name: 'lsl', label: 'Lower Specification Limit (LSL)', type: 'number', min: 0, default: 10, span: 12 },
      { name: 'usl', label: 'Upper Specification Limit (USL)', type: 'number', min: 0, default: 20, span: 12 },
      { name: 'mean', label: 'Process Mean (μ)', type: 'number', min: 0, default: 15, span: 12 },
      { name: 'std_dev', label: 'Process Standard Deviation (σ)', type: 'number', min: 0.0001, default: 1.5, span: 12 }
    ]
  },

  // Exponential Distribution
  exponential: {
    title: 'Exponential Distribution Setup',
    fields: [
      { name: 'lambda_rate', label: 'Mean Arrival / Event Rate (λ)', type: 'number', min: 0.001, step: 0.1, default: 2, span: 12 },
      { name: 'x_value', label: 'Target Value (X)', type: 'number', min: 0, default: 1, span: 12 }
    ]
  },

  // Constant Service Times (M/D/1 Queue)
  constant_service: {
    title: 'Constant Service Times Setup (M/D/1)',
    fields: [
      { name: 'arrival_rate', label: 'Arrival Rate (λ)', type: 'number', min: 0.001, step: 0.1, default: 5, span: 12 },
      { name: 'service_rate', label: 'Service Rate (μ)', type: 'number', min: 0.001, step: 0.1, default: 8, span: 12 }
    ]
  },

  // General Service Times (M/G/1 Queue)
  general_service: {
    title: 'General Service Times Setup (M/G/1)',
    fields: [
      { name: 'arrival_rate', label: 'Arrival Rate (λ)', type: 'number', min: 0.001, step: 0.1, default: 5, span: 12 },
      { name: 'service_rate', label: 'Service Rate (μ)', type: 'number', min: 0.001, step: 0.1, default: 8, span: 12 },
      { name: 'std_dev', label: 'Service Time Standard Deviation (σ)', type: 'number', min: 0, default: 0.05, span: 24 }
    ]
  },

  // Data Analysis
  data_analysis: {
    title: 'Data Analysis Setup',
    fields: [
      { name: 'numItems', label: 'Number of Data Items', type: 'number', min: 1, default: 5 }
    ]
  },

  // Linear Programming
  linear_programming: {
    title: 'Create Linear Programming Data Set',
    fields: [
      { name: 'variables', label: 'Number of Variables', type: 'number', min: 2, default: 2, span: 12 },
      { name: 'constraints', label: 'Number of Constraints', type: 'number', min: 1, default: 2, span: 12 },
      {
        name: 'objective',
        label: 'Objective',
        type: 'radio',
        default: 'max',
        options: [
          { label: 'Maximize', value: 'max' },
          { label: 'Minimize', value: 'min' }
        ]
      }
    ]
  },
  cost_budgeting: {
    title: 'Create Data Set for Cost Budgeting (CPM)',
    fields: [
      { name: 'number_of_activities', label: 'Number of Activities', type: 'number', min: 1, max: 50, default: 4, span: 24 },
      {
        name: 'structure',
        label: 'Network Structure Type',
        type: 'radio',
        default: '1',
        span: 24,
        options: [
          { label: 'Structure 1 (Immediate Predecessor List)', value: '1' },
          { label: 'Structure 2 (Start / End Node Numbers)', value: '2' }
        ]
      }
    ],
    hasRowNaming: true,
    rowNamingLabel: 'Activity Names',
    rowPrefix: 'Activity'
  },

  "43": {
    title: 'Create Data Set for Cost Budgeting (CPM)',
    fields: [
      { name: 'number_of_activities', label: 'Number of Activities', type: 'number', min: 1, max: 50, default: 4, span: 24 },
      {
        name: 'structure',
        label: 'Network Structure Type',
        type: 'radio',
        default: '1',
        span: 24,
        options: [
          { label: 'Structure 1 (Immediate Predecessor List)', value: '1' },
          { label: 'Structure 2 (Start / End Node Numbers)', value: '2' }
        ]
      }
    ],
    hasRowNaming: true,
    rowNamingLabel: 'Activity Names',
    rowPrefix: 'Activity'
  },

  // General Weighting Method
  general_weighting: {
    title: 'Configure General Weighting Method',
    fields: [
      { name: 'numFactors', label: 'Number of Factors', type: 'number', min: 1, default: 3, span: 12 },
      { name: 'numLocations', label: 'Number of Locations', type: 'number', min: 1, default: 2, span: 12 }
    ],
    hasRowNaming: true,
    rowNamingLabel: 'Factor Names',
    rowPrefix: 'Factor',
    hasColNaming: true,
    colNamingLabel: 'Location Names',
    colPrefix: 'Location'
  },

  // Weighting Method (alternative key name)
  weighting_method: {
    title: 'Configure General Weighting Method',
    fields: [
      { name: 'numFactors', label: 'Number of Factors', type: 'number', min: 1, default: 3, span: 12 },
      { name: 'numLocations', label: 'Number of Locations', type: 'number', min: 1, default: 2, span: 12 }
    ],
    hasRowNaming: true,
    rowNamingLabel: 'Factor Names',
    rowPrefix: 'Factor',
    hasColNaming: true,
    colNamingLabel: 'Location Names',
    colPrefix: 'Location'
  },

  // Finite System Size Model
  finite_system_size: {
    title: 'Finite System Size Model Setup',
    fields: [
      { name: 'arrival_rate', label: 'Arrival Rate (λ)', type: 'number', min: 0.001, step: 0.1, default: 5, span: 12 },
      { name: 'service_rate', label: 'Service Rate (μ)', type: 'number', min: 0.001, step: 0.1, default: 10, span: 12 },
      { name: 'servers', label: 'Number of Servers (s)', type: 'number', min: 1, precision: 0, default: 3, span: 12 },
      { name: 'max_system_size', label: 'Maximum System Size (K)', type: 'number', min: 1, precision: 0, default: 3, span: 12 },
      {
        name: 'use_costs',
        label: 'Use Costs',
        type: 'select',
        default: true,
        span: 12,
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false }
        ]
      },
      {
        name: 'time_unit',
        label: 'Time Unit',
        type: 'select',
        default: 'Hours',
        span: 12,
        options: [
          { label: 'Seconds', value: 'Seconds' },
          { label: 'Minutes', value: 'Minutes' },
          { label: 'Hours', value: 'Hours' },
          { label: 'Days', value: 'Days' },
          { label: 'Weeks', value: 'Weeks' },
          { label: 'Months', value: 'Months' },
          { label: 'Quarters', value: 'Quarters' },
          { label: 'Years', value: 'Years' }
        ]
      },
      { name: 'server_cost', label: 'Server Cost', type: 'number', min: 0, default: 12, span: 12 },
      { name: 'waiting_cost', label: 'Waiting Cost', type: 'number', min: 0, default: 12, span: 12 }
    ]
  },

  // Simulation Model
  simulation: {
    title: 'Create Data Set for Simulation',
    fields: [
      { name: 'numCategories', label: 'Number of Categories', type: 'number', min: 2, default: 3, span: 24 }
    ],
    hasRowNaming: true,
    rowNamingLabel: 'Category Names',
    rowPrefix: 'Category'
  }
};
